import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-location';
import confetti from 'canvas-confetti';
import { createFinancingPath } from 'Modules/fortiva/routes';

import Page from 'Core/components/Page';
import { useQueryClient } from 'react-query';
import cacheKeys from 'Core/hooks/queries/cacheKeys';
import { useGetPaymentInfo } from 'Core/hooks/platform/useGetPaymentInfo';
import { formatCurrency } from 'Core/utils/numberFormat';
import { features, postLogMessage, actions } from 'Core/utils/logger';
import { useGetNextStep } from '../hooks/useGetNextStep';
import useGetCustomerInfo from '../hooks/useGetCustomerInfo';
import FinancingPendingDownPayment from '../components/FinancingPendingDownPayment';
import './FinancingSuccess.css';

const DATA = {
  amount: 1249.0,
};

const CheckCircleIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const ReceiptIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M4 2v20l2-1 2 1 2-1 2 1 2-1 2 1 2-1 2 1V2l-2 1-2-1-2 1-2-1-2 1-2-1-2 1-2-1Z" />
    <path d="M16 8h-6a2 2 0 1 0 0 4h4a2 2 0 1 1 0 4H8" />
    <path d="M12 17.5v-11" />
  </svg>
);

const CreditCardIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect width="20" height="14" x="2" y="5" rx="2" />
    <line x1="2" x2="22" y1="10" y2="10" />
  </svg>
);

const RefreshIcon = ({ className = '' }) => (
  <svg
    className={className}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 12a9 9 0 1 1-2.64-6.36" />
    <path d="M21 3v6h-6" />
  </svg>
);

// Statuses this success screen is responsible for rendering. Anything else
// (e.g. a refund surfacing as a RETURN_* status) should go back through
// FinancingRedirect so it can route to the right place.
const HANDLED_ACTIONS = ['DONE', 'FUND_APPLICATION', 'PENDING_DOWN_PAYMENT'];

const ClockHoldIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <circle cx="12" cy="12" r="10" />
    <path d="M12 6v6l3 2" />
  </svg>
);

const Row = ({ label, value }) => (
  <div className="fs-row">
    <span className="fs-row__label">{label}</span>
    <span className="fs-row__value">{value}</span>
  </div>
);

const formatDate = (d = new Date()) => {
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${mm}/${dd}/${d.getFullYear()}`;
};

const fireConfetti = () => {
  const colors = ['#10b981', '#34d399', '#6ee7b7', '#fde68a', '#ffffff'];
  confetti({
    particleCount: 120,
    spread: 80,
    startVelocity: 45,
    origin: { x: 0.5, y: 0.1 },
    colors,
  });
  setTimeout(() => {
    confetti({
      particleCount: 60,
      angle: 60,
      spread: 55,
      origin: { x: 0, y: 0.3 },
      colors,
    });
    confetti({
      particleCount: 60,
      angle: 120,
      spread: 55,
      origin: { x: 1, y: 0.3 },
      colors,
    });
  }, 250);
};

const FinancingSuccess = () => {
  document.title = 'Financing Approved';
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [refetchInterval, setRefetchInterval] = React.useState(false);
  const confettiFiredRef = React.useRef(false);

  const {
    data: application,
    isSuccess: isGetNextStepSuccess,
    refetch,
    isFetching,
  } = useGetNextStep(refetchInterval);
  const { data: paymentInfo } = useGetPaymentInfo();
  const { data: customerInfo, isFetching: isCustomerFetching } = useGetCustomerInfo();

  const {
    next_action,
    external_reference_id = '',
    payload: {
      payment_id = '',
      application_id = '',
      financing_down_payment = 0,
      additional_payment = 0,
      application_status = '',
    } = {},
  } = application || {};

  const isPendingDownPayment = next_action === 'PENDING_DOWN_PAYMENT';
  const isFullyComplete = next_action === 'DONE';
  // Show the "Funds on Hold" section while funding the application or whenever
  // the application is held — including alongside a pending down payment.
  const isFundApplication =
    !isFullyComplete && (next_action === 'FUND_APPLICATION' || application_status === 'Held');

  // Hide the name until the fresh customer-search has settled, so a stale name is never shown.
  const firstName = isCustomerFetching ? '' : customerInfo?.first_name;
  const customerName = isCustomerFetching
    ? ''
    : [customerInfo?.first_name, customerInfo?.last_name].filter(Boolean).join(' ').trim();

  const amount = Number(paymentInfo?.amount_to_pay) || DATA.amount;
  // Same derivation as the finalize summary: backend-computed financed amount,
  // falling back to the total minus the down payment.
  const financedAmount = Number(
    application?.payload?.offer_term?.price?.financed ??
      Math.max(amount - (Number(additional_payment) || 0), 0)
  );
  const formattedAmount = formatCurrency(financedAmount, { withDecimals: true });
  const dateStr = formatDate(new Date());

  // Flush any previously-cached customer-search result so the success screen
  // always refetches fresh and never renders a stale name from a prior application.
  React.useEffect(() => {
    queryClient.removeQueries([cacheKeys.financingCustomerInfo]);
  }, [queryClient]);

  React.useEffect(() => {
    if (confettiFiredRef.current) return;
    if (typeof window === 'undefined') return;
    confettiFiredRef.current = true;
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) return;
    fireConfetti();
  }, []);

  React.useEffect(() => {
    if (isFundApplication) setRefetchInterval(5000);
  }, [isFundApplication]);

  React.useEffect(() => {
    if (isFullyComplete) {
      setRefetchInterval(false);
      queryClient.invalidateQueries(cacheKeys.transactionHistory);
    }
  }, [isFullyComplete, queryClient]);

  // If a manual refresh reveals the status has moved on from what this screen
  // handles (e.g. a refund), hand back to FinancingRedirect to re-route.
  React.useEffect(() => {
    if (next_action && !HANDLED_ACTIONS.includes(next_action)) {
      navigate({ to: createFinancingPath, search: true });
    }
  }, [next_action, navigate]);

  React.useEffect(() => {
    if (isGetNextStepSuccess && payment_id && application_id && external_reference_id) {
      postLogMessage({
        feature: features.financingSignContract,
        action: actions.financingSuccessScreenDisplayed,
        payment_id,
        application_id,
        external_reference_id,
      });
    }
  }, [isGetNextStepSuccess, payment_id, application_id, external_reference_id]);

  return (
    <Page
      title={t('page.financing.header')}
      withStepProgress={false}
      stickyFooter
      branding="fortiva"
    >
      <div className="fs-shell relative">
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isFetching}
          title="Refresh status"
          aria-label="Refresh status"
          data-testid="refresh-status-button"
          className="absolute top-3 right-3 z-10 p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-gray-300 rounded"
        >
          <RefreshIcon className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
        </button>
        <section className="fs-hero fs-anim-fade">
          <div className="fs-hero__icon-wrap">
            <span className="fs-hero__halo" aria-hidden="true" />
            <span className="fs-hero__ping" aria-hidden="true" />
            <span className="fs-hero__icon">
              <CheckCircleIcon />
            </span>
          </div>
          <h1 className="fs-hero__title">
            {firstName ? `Congratulations ${firstName}!` : 'Congratulations!'}
          </h1>
          <p className="fs-hero__subtext">
            Your financing application has been approved. A summary of your transaction is below.
          </p>
          <p className="fs-hero__disclaimer">
            This receipt constitutes future delivery of goods or services that will be provided. You
            will receive your final receipt once your delivery has been received or services
            rendered.
          </p>
          <p className="fs-hero__date">Date: {dateStr}</p>
        </section>

        <section className="fs-section fs-anim-scale" data-testid="payment-summary">
          <header className="fs-section__head">
            <span className="fs-section__icon" aria-hidden="true">
              <CreditCardIcon />
            </span>
            <h2 className="fs-section__title">Payment Summary</h2>
          </header>
          {customerName && <Row label="Account Holder's Name" value={customerName} />}
          <p className="fs-section__paragraph">
            As you agreed, the above transaction, in the amount of{' '}
            <strong>{formattedAmount}</strong>, will be financed by your Fortiva Retail Credit
            Account in accordance with the Terms and Conditions previously provided to you.
          </p>

          <hr className="fs-section__divider" />

          <header
            className="fs-section__head fs-section__head--sub"
            data-testid="transaction-summary"
          >
            <span className="fs-section__icon" aria-hidden="true">
              <ReceiptIcon />
            </span>
            <h3 className="fs-section__title">Transaction Summary</h3>
          </header>
          <Row label="Transaction Date" value={dateStr} />
          <hr className="fs-sep" />
          <Row label="Transaction Amount (Debit)" value={formattedAmount} />

          {isPendingDownPayment && (
            <FinancingPendingDownPayment
              amount={financing_down_payment}
              setRefetchInterval={setRefetchInterval}
            />
          )}

          {isFundApplication && (
            <div className="fs-warning-block" data-testid="funds-on-hold">
              <header className="fs-section__head fs-section__head--sub">
                <span className="fs-section__icon" aria-hidden="true">
                  <ClockHoldIcon />
                </span>
                <div className="fs-section__heading">
                  <span className="fs-eyebrow">Pending Release</span>
                  <h3 className="fs-section__title">Funds on Hold Until Delivery</h3>
                </div>
              </header>
              <p className="fs-section__paragraph">
                Your financing amount of <strong>{formattedAmount}</strong> has been authorized and
                is currently being held. Funds will be released to the provider once your product
                has been delivered or your services have been rendered.
              </p>
            </div>
          )}
        </section>
      </div>
    </Page>
  );
};

export default FinancingSuccess;
